import React, { ChangeEvent, FormEvent, useReducer, useState } from 'react';
import { NButton, NDialog, NSelect } from '@innersource-nn/novoui-react';
import useEnvSalmGroups from 'views/Environments/hooks/useEnvSalmGroups';
import SimpleLoader from 'components/loader/SimpleLoader';
import { ModalProps } from 'types/Modal';
import FormSnackBar from 'components/message/FormSnackBar';
import useUserProfiles from '../hooks/useUserProfiles';
import { Environment } from 'types/Environment';

type FormState = {
    role: string;
    group: string;
};

type FormAction = {
    field: 'role' | 'group';
    event: ChangeEvent<HTMLSelectElement>;
};

const formReducer = (state: FormState, action: FormAction) => {
    return {
        ...state,
        [action.field]: action.event.target.value,
    };
};

export interface UserProfileModalProps extends ModalProps {
    environment: Environment;
}

export default function UserProfileModal({
    visible,
    onClose,
    environment,
}: UserProfileModalProps) {
    const { createUserProfile } = useUserProfiles(environment);
    const [createLoading, setCreateLoading] = useState(false);

    const { loading, roleOptions, groupsOptions } = useEnvSalmGroups(
        environment.environmentUri,
    );
    const [formData, dispatchFormData] = useReducer(formReducer, {
        role: '',
        group: '',
    });

    const [errorMessage, setErrorMessage] = useState<string>('');
    const handleSubmit = async (e: FormEvent) => {
        setCreateLoading(true);

        e.preventDefault();
        const response = await createUserProfile(formData.role, formData.group);
        setErrorMessage(response);
        if (response === '' && onClose) {
            onClose();
        }
        setCreateLoading(false);
    };

    const handleChange = (formAction: FormAction) => {
        setErrorMessage('');
        dispatchFormData(formAction);
    };

    return (
        visible &&
        onClose && (
            <NDialog
                isVisible={visible}
                closeDialog={onClose}
                backgroundClose
                headline="Create new user profile"
                size="medium"
                showDividers={false}
            >
                {loading ? (
                    <SimpleLoader />
                ) : (
                    <div>
                        <form className="w-full text-true-blue">
                            <div className="pb-16 text-sm">
                                Select the role and group your new user
                                profile...
                            </div>
                            <div className="grid grid-cols-2 gap-8">
                                <NSelect
                                    required
                                    name="group"
                                    label="Group"
                                    modelValue={formData.group}
                                    onChange={(
                                        event: ChangeEvent<HTMLSelectElement>,
                                    ) =>
                                        handleChange({ event, field: 'group' })
                                    }
                                    options={groupsOptions}
                                    size="md"
                                    variant="outline"
                                />

                                <NSelect
                                    required
                                    name="role"
                                    label="Role"
                                    modelValue={formData.role}
                                    onChange={(
                                        event: ChangeEvent<HTMLSelectElement>,
                                    ) => handleChange({ event, field: 'role' })}
                                    options={roleOptions
                                        .filter((group) =>
                                            group.environmentGroups.includes(
                                                formData.group,
                                            ),
                                        )
                                        .map((group) => ({
                                            label: group.name,
                                            name: group.name,
                                        }))}
                                    size="md"
                                    variant="outline"
                                />
                            </div>
                            {errorMessage && (
                                <FormSnackBar
                                    title={errorMessage}
                                    iconSize={20}
                                />
                            )}
                        </form>
                        <div className="flex w-full justify-end mt-16 gap-x-8">
                            <NButton square variant="outline" onClick={onClose}>
                                Cancel
                            </NButton>
                            <NButton
                                square
                                onClick={handleSubmit}
                                disabled={
                                    !formData.role ||
                                    !formData.group ||
                                    errorMessage !== ''
                                }
                                isLoading={createLoading}
                            >
                                Create
                            </NButton>
                        </div>
                    </div>
                )}
            </NDialog>
        )
    );
}
